// slices/courseSession/courseSessionSlice.ts
//
// The course the student is on and the module they are studying (Courses V1,
// ora-workspace/docs/courses/FRONTEND-INTEGRATION.md).
//
// Progress is explicit, as the backend wants it: opening a step calls
// `start` and keeps its launch; ending an activity calls `complete`, and the
// server's answer — the new progress, or what is still missing — is what the
// session renders. Nothing here decides doneness.
//
// Not persisted: the payloads are server-authoritative, so a reload re-reads
// them rather than rehydrating a stale copy.

import {
  createAsyncThunk,
  createSelector,
  createSlice,
  type PayloadAction,
  type SerializedError,
} from '@reduxjs/toolkit';
import type { RootState } from '../../store/store';
import { logout } from '../auth/authSlice';
import { examsSelectors } from '../exams/examsSlice';
import {
  completeCourseStep,
  getCourse,
  getCourseModule,
  listCourses,
  startCourseStep,
  type CourseStepCompleteOutcome,
} from '../../services/courses';
import type {
  CourseDetail,
  CourseModuleDetail,
  CourseStepLaunch,
  CourseStepMissing,
  CourseStepStartResult,
  CourseStepSummary,
  CourseSummary,
} from '../../types/course';
import {
  entryStepId,
  isStepReachable,
  nextStepId,
  resumeModule,
} from './courseSessionRules';

export type CourseLoadStatus = 'idle' | 'loading' | 'ready' | 'error';

/** `step` while an activity is on screen; `module-complete` past the last one. */
export type CourseSessionPhase = 'step' | 'module-complete';

export interface CourseStepRefusal {
  message: string;
  missing: CourseStepMissing;
}

export interface CourseSessionState {
  /** Every course the student can open, as last listed. */
  courses: CourseSummary[];
  course: CourseDetail | null;
  courseStatus: CourseLoadStatus;
  module: CourseModuleDetail | null;
  moduleStatus: CourseLoadStatus;
  activeStepId: string | null;
  phase: CourseSessionPhase;
  /** What `start` returned, per step opened this session. */
  launches: Record<string, CourseStepLaunch>;
  /** What `complete` last refused with, per step; cleared on success. */
  refusals: Record<string, CourseStepRefusal>;
  isCompleting: boolean;
  error: string | null;
}

const initialState: CourseSessionState = {
  courses: [],
  course: null,
  courseStatus: 'idle',
  module: null,
  moduleStatus: 'idle',
  activeStepId: null,
  phase: 'step',
  launches: {},
  refusals: {},
  isCompleting: false,
  error: null,
};

/** What opening a module afresh resolves with. */
type OpenedModule = {
  /** Absent when the opener did not re-list them; the last list stays. */
  courses?: CourseSummary[];
  course: CourseDetail | null;
  module: CourseModuleDetail | null;
  stepId: string | null;
};

/** A rejected thunk carries a serialized error, not an `Error` instance. */
const errorMessage = (error: SerializedError): string =>
  error.message || 'Something went wrong';

/** The course on the student's primary exam, else the first one listed. */
export const pickCurrentCourse = (
  courses: CourseSummary[],
  primaryExamId: string | null | undefined
): CourseSummary | null =>
  courses.find((course) => course.examId === primaryExamId) ??
  courses[0] ??
  null;

/**
 * The student's courses and the current one: the course on their primary
 * exam (the exam selector switches it), else the first listed. Null when none.
 */
const readCurrentCourse = async (
  state: RootState
): Promise<{ courses: CourseSummary[]; course: CourseDetail | null }> => {
  const courses = await listCourses();

  const current = pickCurrentCourse(
    courses,
    examsSelectors.selectPrimaryExam(state)?.id
  );

  return { courses, course: current ? await getCourse(current.id) : null };
};

export const loadCourse = createAsyncThunk<
  { courses: CourseSummary[]; course: CourseDetail | null },
  void,
  { state: RootState }
>('courseSession/loadCourse', (_, { getState }) =>
  readCurrentCourse(getState())
);

/**
 * Open the module the server's `resume` pointer names. Re-reads the course
 * first so Continue Ahead after a finished module sees the next one.
 * Resolves a null module when the course is complete.
 */
export const startNextModule = createAsyncThunk<
  {
    courses: CourseSummary[];
    course: CourseDetail | null;
    module: CourseModuleDetail | null;
    stepId: string | null;
  },
  void,
  { state: RootState }
>('courseSession/startNextModule', async (_, { getState }) => {
  const { courses, course } = await readCurrentCourse(getState());
  const target = resumeModule(course);
  if (!course || !target)
    return { courses, course, module: null, stepId: null };

  const module = await getCourseModule(course.id, target.id);
  return { courses, course, module, stepId: course.resume?.stepId ?? null };
});

/**
 * Open the session at one step of a named course and module, read fresh: the
 * way back from a page outside the session (a module quiz's self-assessment
 * pages) to the step it was opened from. The course and the module are read
 * together (both routes take the course id or slug). The course list is read
 * only when the store has none (a cold reload), for the header's switcher.
 * Rejects when the course or module is unknown (404).
 */
export const openCourseStepAt = createAsyncThunk<
  OpenedModule,
  { courseKey: string; moduleId: string; stepId: string },
  { state: RootState }
>(
  'courseSession/openStepAt',
  async ({ courseKey, moduleId, stepId }, { getState }) => {
    const hasCourses = getState().courseSession.courses.length > 0;

    const [course, module, courses] = await Promise.all([
      getCourse(courseKey),
      getCourseModule(courseKey, moduleId),
      hasCourses ? Promise.resolve(undefined) : listCourses(),
    ]);

    return { course, module, stepId, courses };
  }
);

/** `start` a step: idempotent, and what hands the body its launch. */
export const openCourseStep = createAsyncThunk<
  { stepId: string; result: CourseStepStartResult },
  string,
  { state: RootState }
>('courseSession/openStep', async (stepId, { getState }) => {
  const courseId = getState().courseSession.course?.id;
  if (!courseId) throw new Error('No course is active');

  return { stepId, result: await startCourseStep(courseId, stepId) };
});

/** `complete` the active step; the server checks the rule and answers. */
export const completeActiveStep = createAsyncThunk<
  { stepId: string; outcome: CourseStepCompleteOutcome },
  void,
  { state: RootState }
>(
  'courseSession/completeActiveStep',
  async (_, { getState }) => {
    const { course, activeStepId } = getState().courseSession;
    if (!course || !activeStepId) throw new Error('No course step is active');

    return {
      stepId: activeStepId,
      outcome: await completeCourseStep(course.id, activeStepId),
    };
  },
  {
    // One `complete` at a time: a video step asks after every answer, and two
    // in flight could land out of order (a refusal over the completion).
    // Skipped without a `rejected` action, so no error is recorded.
    condition: (_, { getState }) => !getState().courseSession.isCompleting,
  }
);

/** Re-read the active module. */
export const refreshCourseModule = createAsyncThunk<
  CourseModuleDetail,
  void,
  { state: RootState }
>('courseSession/refreshModule', async (_, { getState }) => {
  const { course, module } = getState().courseSession;
  if (!course || !module) throw new Error('No course module is active');

  return getCourseModule(course.id, module.id);
});

/** Write a step's fresh summary into the module and the course outline. */
const mergeStep = (state: CourseSessionState, step: CourseStepSummary) => {
  if (state.module) {
    state.module.steps = state.module.steps.map((entry) =>
      entry.id === step.id ? { ...entry, ...step } : entry
    );
  }

  if (state.course) {
    state.course.modules = state.course.modules.map((module) => ({
      ...module,
      steps: module.steps.map((entry) => (entry.id === step.id ? step : entry)),
    }));
  }
};

const applyModule = (
  state: CourseSessionState,
  module: CourseModuleDetail,
  preferredStepId: string | null = null
) => {
  state.module = module;
  state.moduleStatus = 'ready';
  state.error = null;

  const has = (stepId: string | null) =>
    stepId !== null && module.steps.some((step) => step.id === stepId);

  if (has(preferredStepId)) {
    state.activeStepId = preferredStepId;
  } else if (!has(state.activeStepId)) {
    state.activeStepId = entryStepId(module.steps);
  }
};

/** A module opened afresh (`startNextModule`, `openCourseStepAt`). */
const applyOpenedModule = (
  state: CourseSessionState,
  payload: OpenedModule
) => {
  if (payload.courses) state.courses = payload.courses;
  state.course = payload.course;
  state.courseStatus = 'ready';
  state.phase = 'step';
  state.activeStepId = null;
  state.launches = {};
  state.refusals = {};
  if (payload.module) {
    applyModule(state, payload.module, payload.stepId);
  } else {
    state.module = null;
    state.moduleStatus = 'ready';
  }
};

const courseSessionSlice = createSlice({
  name: 'courseSession',
  initialState,
  reducers: {
    /** Open a step from the rail. Ignored for a step the rail does not offer. */
    selectCourseStep: (state, action: PayloadAction<string>) => {
      if (!state.module) return;
      if (!isStepReachable(state.module.steps, action.payload)) return;
      state.activeStepId = action.payload;
      state.phase = 'step';
    },
    /** Continue from the current step: the next incomplete one, or the end. */
    continueCourseStep: (state) => {
      if (!state.module) return;
      const next = nextStepId(state.module.steps, state.activeStepId);
      if (next) {
        state.activeStepId = next;
      } else {
        state.phase = 'module-complete';
      }
    },
    clearCourseSession: () => initialState,
    /**
     * A stage's own change to its launch (a practice conversation's sessions),
     * so coming back to the step shows it as it is now, not as `start` first
     * returned it.
     */
    courseStepLaunchUpdated: (
      state,
      action: PayloadAction<{ stepId: string; launch: CourseStepLaunch }>
    ) => {
      if (!state.launches[action.payload.stepId]) return;
      state.launches[action.payload.stepId] = action.payload.launch;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadCourse.pending, (state) => {
        state.courseStatus = 'loading';
      })
      .addCase(loadCourse.fulfilled, (state, action) => {
        state.courses = action.payload.courses;
        state.course = action.payload.course;
        state.courseStatus = 'ready';
        state.error = null;
      })
      .addCase(loadCourse.rejected, (state, action) => {
        state.courseStatus = 'error';
        state.error = errorMessage(action.error);
      })
      .addCase(startNextModule.pending, (state) => {
        state.moduleStatus = 'loading';
        state.courseStatus = 'loading';
      })
      .addCase(startNextModule.fulfilled, (state, action) => {
        applyOpenedModule(state, action.payload);
      })
      .addCase(startNextModule.rejected, (state, action) => {
        state.moduleStatus = 'error';
        state.courseStatus = state.course ? 'ready' : 'error';
        state.error = errorMessage(action.error);
      })
      .addCase(openCourseStepAt.pending, (state) => {
        state.moduleStatus = 'loading';
        state.courseStatus = 'loading';
      })
      .addCase(openCourseStepAt.fulfilled, (state, action) => {
        applyOpenedModule(state, action.payload);
      })
      .addCase(openCourseStepAt.rejected, (state, action) => {
        state.moduleStatus = 'error';
        state.courseStatus = state.course ? 'ready' : 'error';
        state.error = errorMessage(action.error);
      })
      .addCase(openCourseStep.fulfilled, (state, action) => {
        const { stepId, result } = action.payload;
        state.launches[stepId] = result.launch;
        mergeStep(state, result.step);
        state.error = null;
      })
      .addCase(openCourseStep.rejected, (state, action) => {
        state.error = errorMessage(action.error);
      })
      .addCase(completeActiveStep.pending, (state) => {
        state.isCompleting = true;
      })
      .addCase(completeActiveStep.fulfilled, (state, action) => {
        state.isCompleting = false;
        state.error = null;
        const { stepId, outcome } = action.payload;

        if (!outcome.completed) {
          state.refusals[stepId] = {
            message: outcome.message,
            missing: outcome.missing,
          };

          return;
        }

        delete state.refusals[stepId];
        mergeStep(state, outcome.result.step);

        if (state.module && state.module.id === outcome.result.module.id) {
          state.module.completedStepCount =
            outcome.result.module.completedStepCount;

          state.module.stepCount = outcome.result.module.stepCount;
        }

        if (state.course && state.course.id === outcome.result.course.id) {
          state.course.completedStepCount =
            outcome.result.course.completedStepCount;

          state.course.stepCount = outcome.result.course.stepCount;
          state.course.resume = outcome.result.course.resume;

          state.course.modules = state.course.modules.map((module) =>
            module.id === outcome.result.module.id
              ? {
                  ...module,
                  completedStepCount: outcome.result.module.completedStepCount,
                  stepCount: outcome.result.module.stepCount,
                }
              : module
          );
        }
      })
      .addCase(completeActiveStep.rejected, (state, action) => {
        state.isCompleting = false;
        state.error = errorMessage(action.error);
      })
      .addCase(refreshCourseModule.fulfilled, (state, action) => {
        applyModule(state, action.payload);
      })
      .addCase(refreshCourseModule.rejected, (state, action) => {
        state.error = errorMessage(action.error);
      })
      .addCase(logout, () => initialState);
  },
});

export const {
  selectCourseStep,
  continueCourseStep,
  clearCourseSession,
  courseStepLaunchUpdated,
} = courseSessionSlice.actions;

export const selectCourseSession = (state: RootState) => state.courseSession;

export const selectCourses = (state: RootState) => state.courseSession.courses;

export const selectCourse = (state: RootState) => state.courseSession.course;

export const selectCourseStatus = (state: RootState) =>
  state.courseSession.courseStatus;

export const selectCourseModule = (state: RootState) =>
  state.courseSession.module;

export const selectCourseModuleStatus = (state: RootState) =>
  state.courseSession.moduleStatus;

export const selectCourseSessionPhase = (state: RootState) =>
  state.courseSession.phase;

export const selectCourseSessionError = (state: RootState) =>
  state.courseSession.error;

export const selectIsCompletingStep = (state: RootState) =>
  state.courseSession.isCompleting;

const selectActiveStepId = (state: RootState) =>
  state.courseSession.activeStepId;

export const selectActiveCourseStep = createSelector(
  [selectCourseModule, selectActiveStepId],
  (module, activeStepId) =>
    module?.steps.find((step) => step.id === activeStepId) ?? null
);

export const selectActiveStepLaunch = (state: RootState) => {
  const stepId = state.courseSession.activeStepId;
  return stepId ? (state.courseSession.launches[stepId] ?? null) : null;
};

export const selectActiveStepRefusal = (state: RootState) => {
  const stepId = state.courseSession.activeStepId;
  return stepId ? (state.courseSession.refusals[stepId] ?? null) : null;
};

/** The module Continue Ahead opens next, from the server's `resume` pointer. */
export const selectNextCourseModule = createSelector([selectCourse], (course) =>
  resumeModule(course)
);

export default courseSessionSlice.reducer;
